import { useState, useCallback, useRef, useEffect } from "react";

export interface UseChatUIReturn {
  // State
  showChat: boolean;
  isChatFullScreen: boolean;
  chatInput: string;
  isSending: boolean;

  // Refs
  messagesEndRef: React.RefObject<HTMLDivElement>;

  // Actions
  setShowChat: (show: boolean) => void;
  setIsChatFullScreen: (fullScreen: boolean) => void;
  setChatInput: (input: string) => void;
  setIsSending: (sending: boolean) => void;
  closeChat: () => void;
  clearInput: () => void;
  restoreInput: (message: string) => void;
  scrollToBottom: () => void;
}

/**
 * Hook for managing chat UI state.
 * Handles visibility, fullscreen mode, input, and scroll behavior.
 */
export function useChatUI(): UseChatUIReturn {
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // UI state
  const [showChat, setShowChat] = useState(false);
  const [isChatFullScreen, setIsChatFullScreen] = useState(false);
  const [chatInput, setChatInput] = useState("");
  const [isSending, setIsSending] = useState(false);

  const closeChat = useCallback(() => {
    setShowChat(false);
    setIsChatFullScreen(false);
  }, []);

  const clearInput = useCallback(() => {
    setChatInput("");
  }, []);

  const restoreInput = useCallback((message: string) => {
    setChatInput(message);
  }, []);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  return {
    // State
    showChat,
    isChatFullScreen,
    chatInput,
    isSending,

    // Refs
    messagesEndRef,

    // Actions
    setShowChat,
    setIsChatFullScreen,
    setChatInput,
    setIsSending,
    closeChat,
    clearInput,
    restoreInput,
    scrollToBottom,
  };
}

/**
 * Hook to auto-scroll when messages change.
 */
export function useChatAutoScroll(
  messagesEndRef: React.RefObject<HTMLDivElement>,
  dependency: unknown
): void {
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messagesEndRef, dependency]);
}
